import { parse, stringify } from "yaml";

type Origin = "observed" | "manual" | "suggested";
interface TokenEvidence { value: string; suggestedRole?: string }
export interface DesignValues {
  colors: Array<{ role: string; value: string; manual?: boolean }>;
  fonts: string[];
  observedColors: string[];
  observedFonts: string[];
  manualFonts?: number[];
  name?: string;
}

export const DESIGN_COLOR_DEFAULTS: Record<string, string> = {
  primary: "#111111", secondary: "#6B7280", accent: "#7B2FF7",
  background: "#FFFFFF", surface: "#FFFFFF", border: "#E5E5E5", "on-accent": "#FFFFFF",
};

export function observedColorRoles(colors: string[], evidence: TokenEvidence[]) {
  const used = new Set<string>();
  return colors.map(value => {
    const preferred = evidence.find(item => item.value === value)?.suggestedRole;
    const role = preferred && preferred in DESIGN_COLOR_DEFAULTS && !used.has(preferred)
      ? preferred : Object.keys(DESIGN_COLOR_DEFAULTS).find(key => !used.has(key)) || "accent";
    used.add(role);
    return { role, value };
  });
}

/** Serialize the document and its guidance from one final token model. */
export function synchronizeDesignDocument(markdown: string, values: DesignValues, language: "en" | "de") {
  const match = markdown.match(/^---\s*\n([\s\S]*?)\n---(?:\s*\n|$)/);
  if (!match) throw new Error("Missing DESIGN.md front matter");
  const doc = parse(match[1], { maxAliasCount: 0 });
  if (!doc || typeof doc !== "object" || Array.isArray(doc)) throw new Error("Invalid DESIGN.md front matter");
  const colors = { ...DESIGN_COLOR_DEFAULTS };
  const origins: Record<string, Origin> = Object.fromEntries(Object.keys(colors).map(key => [key, "suggested"]));
  for (const color of values.colors) {
    if (!Object.hasOwn(DESIGN_COLOR_DEFAULTS, color.role) || !/^#(?:[\da-f]{3}|[\da-f]{6}|[\da-f]{8})$/i.test(color.value)) continue;
    colors[color.role] = color.value.toUpperCase();
    origins[color.role] = color.manual ? "manual" : values.observedColors.some(v => v.toLowerCase() === color.value.toLowerCase()) ? "observed" : "suggested";
  }
  doc.colors = colors;
  if (values.name) doc.name = values.name;
  const fonts = values.fonts.map((value, index) => ({value, index})).filter(font => font.value.trim());
  const selectedFonts = fonts.length ? fonts : [{value:"Inter",index:-1},{value:"Geist",index:-1}];
  const typography = doc.typography && typeof doc.typography === "object" ? doc.typography : {};
  let index = 0;
  const fontRows: string[] = [];
  const labels = language === "de"
    ? { observed: "Erkannt", manual: "Manuell angepasst", suggested: "Vorgeschlagen" }
    : { observed: "Observed", manual: "Manually adjusted", suggested: "Suggested" };
  const inline = (value: unknown) => String(value).replace(/[\r\n|`]/g, " ");
  for (const [role, properties] of Object.entries(typography)) {
    if (!properties || typeof properties !== "object" || Array.isArray(properties)) continue;
    const fontIndex = Math.min(index++, selectedFonts.length - 1);
    const {value: font, index: originalIndex} = selectedFonts[fontIndex];
    (properties as Record<string, unknown>).fontFamily = font;
    const origin: Origin = values.manualFonts?.includes(originalIndex) && fonts.length ? "manual"
      : values.observedFonts.includes(font) ? "observed" : "suggested";
    fontRows.push(`- typography.${inline(role)}: ${inline(font)} (${labels[origin]})`);
  }
  doc.typography = typography;
  const de = language === "de";
  const sections = [
    ["Overview", de ? `Design-Leitplanke für ${inline(doc.name)} aus öffentlich sichtbaren Website-Hinweisen. Nur als erkannt markierte Werte sind durch die Quelle belegt. Vorschläge sind ersetzbare Standardwerte, keine beobachteten Markenmerkmale.` : `Design guidance for ${inline(doc.name)} from public website evidence. Only values marked observed are supported by source evidence. Suggestions are replaceable defaults, not observed brand attributes.`],
    ["Colors", Object.entries(colors).map(([role, value]) => `- colors.${role}: ${value} (${labels[origins[role]]})`).join("\n")],
    ["Typography", fontRows.join("\n")],
    ["Layout", de ? "Abstände aus spacing verwenden. Nicht belegte Layoutentscheidungen sind Vorschläge." : "Use spacing tokens. Layout decisions without evidence are suggestions."],
    ["Elevation & Depth", de ? "Surface und Border aus colors verwenden. Zusätzliche Schatten sind nicht als erkannt belegt." : "Use the surface and border color tokens. Additional shadows are not observed evidence."],
    ["Shapes", de ? "Die Radien aus rounded sind die maßgebliche Vorgabe." : "The rounded tokens are the authoritative radius values."],
    ["Components", de ? "Komponenten verwenden die Token-Referenzen im YAML. Farben und Schriften aus den obigen Listen übernehmen." : "Components use the YAML token references. Use the colors and fonts listed above."],
    ["Do's and Don'ts", de ? "Erkannte Werte bewahren, manuelle Anpassungen übernehmen und Vorschläge vor Verwendung prüfen. Keine zusätzlichen Markenmerkmale aus Standardwerten ableiten." : "Preserve observed values, apply manual adjustments, and review suggestions before use. Do not infer additional brand attributes from defaults."],
  ];
  return `---\n${stringify(doc, { lineWidth: 0 })}---\n\n${sections.map(([title, body]) => `## ${title}\n\n${body}`).join("\n\n")}`;
}

export function replaceYamlBlock(frontmatter: string, key: string, lines: string[]) {
  const block = `${key}:\n${lines.join("\n")}`;
  const pattern = new RegExp(`^${key}:\\n(?:  .+(?:\\n|$))+`, "m");
  return pattern.test(frontmatter) ? frontmatter.replace(pattern, `${block}\n`) : `${frontmatter}\n${block}`;
}
